import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { primeJdCache, primeFormCache } from "@/lib/publicCache";

export default function Landing() {
    const [openings, setOpenings] = useState([]);
    const [loading, setLoading] = useState(true);

    const totalOpenings = useMemo(
        () => openings.reduce((s, r) => s + (r.openings_count || 0), 0),
        [openings],
    );

    // Flatten every role → each JD becomes a single row.
    const rows = useMemo(() => {
        const out = [];
        openings.forEach((r) => {
            (r.descriptions || []).forEach((jd) => {
                out.push({
                    jd_id: jd.id,
                    jd_title: jd.job_title,
                    role_id: r.id,
                    role_name: r.name,
                });
            });
        });
        return out;
    }, [openings]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/public/openings");
                setOpenings(data);
                // Prefetch all JDs + forms in the background so the next
                // click into a JD or Apply form hydrates instantly.
                const { prefetchAllPublic } = await import(
                    "@/lib/publicCache"
                );
                prefetchAllPublic(data);
            } catch (e) {
                toast.error(apiErrorMessage(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div
            data-testid="candidate-page"
            className="min-h-screen bg-[#F7F5F0] text-[#0F1F38] font-sans"
        >
            <header
                data-testid="candidate-header"
                className="border-b border-[#0F1F38]/10 bg-[#F7F5F0]/95 backdrop-blur-xl sticky top-0 z-30"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <a
                        href="/"
                        data-testid="brand-logo"
                        className="inline-flex items-center"
                    >
                        <img
                            src="/blubridge-logo.png"
                            alt="BluBridge"
                            className="h-6 md:h-7 w-auto"
                        />
                    </a>
                    <span className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                        Careers
                    </span>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-16">
                <div
                    data-testid="page-heading"
                    className="mb-10 md:mb-14 flex items-end justify-between gap-6"
                >
                    <div>
                        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#0F1F38]">
                            Join Us
                        </h1>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/50 mb-1">
                            Currently hiring
                        </div>
                        <div
                            data-testid="hero-openings-count"
                            className="font-display text-3xl md:text-4xl leading-none text-[#0F1F38]"
                        >
                            {loading ? "—" : totalOpenings}
                        </div>
                    </div>
                </div>

                <div
                    data-testid="openings-table"
                    className="border border-[#0F1F38]/15 bg-white"
                >
                    <div className="grid grid-cols-12 border-b border-[#0F1F38]/15 bg-[#EAE5D9]/40">
                        <div className="col-span-8 md:col-span-9 px-5 py-3 text-[10px] tracking-label uppercase text-[#0F1F38]/70 font-semibold">
                            Job Role
                        </div>
                        <div className="col-span-4 md:col-span-3 px-5 py-3 text-[10px] tracking-label uppercase text-[#0F1F38]/70 font-semibold text-right">
                            Action
                        </div>
                    </div>

                    {loading ? (
                        <div
                            data-testid="openings-loading"
                            className="flex items-center gap-3 text-sm text-[#0F1F38]/70 px-5 py-8"
                        >
                            <Loader2 size={16} className="animate-spin" />
                            Loading openings…
                        </div>
                    ) : rows.length === 0 ? (
                        <div
                            data-testid="openings-empty"
                            className="px-5 py-16 text-center"
                        >
                            <div className="font-display text-2xl md:text-3xl text-[#0F1F38] mb-2">
                                No open roles right now.
                            </div>
                            <p className="text-sm text-[#0F1F38]/70">
                                Write to us at{" "}
                                <a
                                    href="mailto:careers@blubridge.com"
                                    className="link-underline text-[#0F1F38]"
                                >
                                    careers@blubridge.com
                                </a>
                                .
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-[#0F1F38]/10">
                            {rows.map((row) => (
                                <div
                                    key={row.jd_id}
                                    data-testid={`jd-row-${row.jd_id}`}
                                    className="grid grid-cols-12 items-center hover:bg-[#F7F5F0]/60 transition-colors"
                                >
                                    <div className="col-span-8 md:col-span-9 px-5 py-4">
                                        <a
                                            href={`/jd/${row.jd_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`jd-open-${row.jd_id}`}
                                            className="font-display text-lg md:text-xl text-[#0F1F38] link-underline"
                                        >
                                            {row.jd_title}
                                        </a>
                                    </div>
                                    <div className="col-span-4 md:col-span-3 px-5 py-4 flex justify-end">
                                        <a
                                            href={`/jd/${row.jd_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`jd-apply-${row.jd_id}`}
                                            className="inline-flex items-center gap-2 px-4 h-9 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                                        >
                                            Apply
                                            <ArrowUpRight size={12} />
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-10 h-2"></div>
            </main>
        </div>
    );
}
